import { useMemo, useState } from "react"

import { threadSenderAddress } from "@productlane/lib/src/email-settings"

import { useZeroWorkspace } from "@/lib/zero"
import { useThreadContext } from "../../../thread-context"

export interface FromAddressOption {
	id: string
	email: string
	senderName: string | null
	isPrimary: boolean
	isEnabled: boolean
}

// Only addresses that can receive are offered: replying from a send only
// broadcast address would strand the customer's answer.
export function useFromAddress() {
	const workspace = useZeroWorkspace()
	const { thread } = useThreadContext()

	const addresses = useMemo<FromAddressOption[]>(
		() =>
			(workspace?.emailSettings ?? [])
				.filter((address) => address.isEnabled)
				.map((address) => ({
					id: address.id,
					email: address.primaryEmail,
					senderName: address.senderName ?? null,
					isPrimary: address.isPrimary,
					isEnabled: address.isEnabled,
				}))
				.sort((a, b) => {
					if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1
					return a.email.localeCompare(b.email)
				}),
		[workspace?.emailSettings],
	)

	const portalAddressId = useMemo(
		() =>
			workspace?.portalInstances?.find(
				(portal) => portal.id === thread.portalInstanceId,
			)?.defaultEmailAddressId ?? null,
		[workspace?.portalInstances, thread.portalInstanceId],
	)

	// Same order the mutator sends with, so the picker shows the real From.
	const defaultId = useMemo(
		() =>
			threadSenderAddress(addresses, {
				threadAddressId: thread.emailAddressId,
				portalAddressId,
			})?.id ?? null,
		[addresses, thread.emailAddressId, portalAddressId],
	)

	const [overrideId, setFromAddressId] = useState<string | null>(null)

	const fromAddressId =
		overrideId && addresses.some((a) => a.id === overrideId)
			? overrideId
			: defaultId

	const fromAddress =
		addresses.find((address) => address.id === fromAddressId) ?? null

	return {
		addresses,
		fromAddressId,
		fromAddress,
		setFromAddressId,
		// One address is the status quo, so the picker stays hidden.
		showPicker: addresses.length > 1,
	}
}
